// ── AWARD SETUP ──────────────────────────────────────────────
// The work between a Notice of Award arriving and the money being spent, in
// the order an office actually does it:
//   1. Notice of Award — read the NOA, confirm it, see the clock start
//   2. Budget Call     — split the ceiling, collect drafts, consolidate, trim
//   3. Agreements      — one template out to many sites, and track the returns
//
// Written for a grants officer: no jargon, every figure visible, and each
// screen says what it will do before it does it. The rules live on the
// server (lib/award.js, lib/budgetCall.js, lib/agreements.js).
import { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { API, apiFetch } from "../utils/api";
import BudgetVsActualTab from "./BudgetVsActualTab";
import CashTab from "./CashTab";

const TABS = [
  { id: "noa",        label: "Notice of Award" },
  { id: "call",       label: "Budget Call" },
  { id: "agreements", label: "Agreements" },
  { id: "actuals",    label: "Budget vs Actual" },
  { id: "cash",       label: "Cash and SF-425" },
];

const AMBER = "#d97706", RED = "#dc2626", GREEN = "#059669";

const money = (n, cur = "USD") => {
  const sym = { USD: "$", NGN: "₦", EUR: "€", GBP: "£" }[cur] || "";
  return sym + Math.round(Number(n || 0)).toLocaleString();
};
const dateLong = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};
function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function AwardSetupPage() {
  const T = useT();
  const [tab, setTab] = useState("noa");
  // The confirmed award is the spine: the budget call splits its ceiling and
  // agreements inherit its period, so it is held here and passed down.
  const [award, setAward] = useState(null);
  const [funders, setFunders] = useState([]);

  useEffect(() => {
    apiFetch(`${API}/funders`).then((r) => r.json()).then((d) => setFunders(d.funders || [])).catch(() => {});
  }, []);

  const card  = { background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  const label = { fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "9px 11px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textBody, fontSize: 13, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" };
  const btn = (primary) => ({ padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 38, border: `1px solid ${primary ? TOKENS.gold : T.border}`, background: primary ? TOKENS.gold : "transparent", color: primary ? "#fff" : T.textBody });
  const shared = { T, card, label, input, btn, funders, award, setAward };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", background: T.bgApp }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h1 style={{ fontFamily: FONT_H, fontSize: 24, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>Award Setup</h1>
        <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted, margin: "0 0 20px" }}>
          Read a Notice of Award and start its clock, run the budget call across departments, and send agreements out to your sites.
        </p>

        <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 22, overflowX: "auto" }}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "10px 16px", background: "none", border: "none", cursor: "pointer",
              fontFamily: FONT_UI, fontSize: 13.5, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? T.text : T.textMuted,
              borderBottom: `2px solid ${tab === t.id ? TOKENS.gold : "transparent"}`, marginBottom: -1,
            }}>{t.label}</button>
          ))}
        </div>

        {tab === "noa"        && <NoticeOfAward {...shared} />}
        {tab === "call"       && <BudgetCall {...shared} />}
        {tab === "agreements" && <Agreements {...shared} />}
        {tab === "actuals"    && <BudgetVsActualTab {...shared} />}
        {tab === "cash"       && <CashTab {...shared} />}
      </div>
    </div>
  );
}

// ── TAB 1 — NOTICE OF AWARD ──────────────────────────────────
function NoticeOfAward({ T, card, label, input, btn, funders, award, setAward }) {
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [result, setResult] = useState(null);

  const parse = async (file) => {
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(`${API}/award/parse`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "NofoDesk could not read that file.");
      setResult(data);
      setAward(data.award);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  // Any correction on the confirm screen re-dates everything, without AI.
  const recompute = useCallback(async (next, funderId) => {
    setAward(next);
    try {
      const res = await apiFetch(`${API}/award/schedule`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ award: next, funderId: funderId || result?.funder }),
      });
      const data = await res.json();
      if (res.ok) setResult((r) => ({ ...r, ...data }));
    } catch { /* the figures on screen are still the confirmed ones */ }
  }, [result?.funder, setAward]);

  const [saving, setSaving]     = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const saveAward = async () => {
    setSaving(true); setSavedMsg("");
    try {
      const id = award.id || `${(award.awardNumber || "award").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}-${Math.random().toString(36).slice(2, 7)}`;
      const res = await apiFetch(`${API}/setup/awards/${encodeURIComponent(id)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...award, id, funderId: award.funderId || result?.funder }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not save the award.");
      setAward({ ...award, id });
      setSavedMsg("Saved.");
    } catch (e) { setSavedMsg(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={card}>
        <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 6px" }}>Read the Notice of Award</h2>
        <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, margin: "0 0 14px", lineHeight: 1.55 }}>
          Upload the NOA as a PDF. NofoDesk reads the approved budget, the indirect term, restricted funds, what the funder has asked you to revise, and every date the notice imposes. Nothing is saved until you have checked it.
        </p>
        <label style={{ ...btn(true), display: "inline-block" }}>
          {busy ? "Reading…" : "Choose the NOA"}
          <input type="file" accept=".pdf,.docx,.txt" style={{ display: "none" }} disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) parse(f); e.target.value = ""; }} />
        </label>
        {error && <div style={{ marginTop: 12, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}
      </div>

      {result && (
        <>
          {/* What must be true before this becomes the ceiling for everything else. */}
          {!!result.checks?.length && (
            <div style={card}>
              <span style={label}>Check these</span>
              {result.checks.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: i ? `1px solid ${T.border}` : "none" }}>
                  <span style={{ color: c.level === "error" ? RED : AMBER, fontWeight: 700, fontSize: 12, fontFamily: FONT_MONO, minWidth: 56 }}>
                    {c.level === "error" ? "CHECK" : "NOTE"}
                  </span>
                  <span style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, lineHeight: 1.5 }}>{c.message}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
            <Figure {...{ T, card, label }} title="Total awarded" value={money(award?.amounts?.total)} note={award?.awardNumber} />
            <Figure {...{ T, card, label }} title="Direct cost" value={money(award?.amounts?.direct)} note={`${award?.objectClasses?.length || 0} object classes`} />
            <Figure {...{ T, card, label }} title="Indirect" value={money(award?.amounts?.indirect)}
              note={result.indirectBase ? `${result.indirectBase.effectiveRatePct}% of direct · base ${result.indirectBase.base}` : ""} />
          </div>

          {/* The check that catches the mistake before it is made. */}
          {result.indirectBase?.base === "MTDC" && (
            <div style={{ ...card, borderLeft: `3px solid ${AMBER}` }}>
              <span style={label}>The indirect base</span>
              <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textBody, margin: 0, lineHeight: 1.6 }}>{result.indirectBase.note}</p>
              <p style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "10px 0 0" }}>
                Budgets you build from this award will use the modified base, so the indirect line matches what the funder approved.
              </p>
            </div>
          )}

          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
              <span style={label}>What happens next, and when</span>
              <span style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted }}>
                {funders.find((f) => f.id === result.funder)?.short || result.funderName}
              </span>
            </div>
            {(result.schedule || []).map((item) => (
              <div key={item.id} style={{ display: "flex", gap: 12, alignItems: "baseline", padding: "10px 0", borderTop: `1px solid ${T.border}` }}>
                <span style={{ minWidth: 92, fontFamily: FONT_MONO, fontSize: 11.5, color: item.status === "overdue" ? RED : item.status === "due" ? AMBER : T.textMuted }}>
                  {item.due ? dateLong(item.due) : "standing"}
                </span>
                <span style={{ flex: 1, fontFamily: FONT_UI, fontSize: 13.5, color: T.textBody }}>
                  {item.label}
                  {item.amount != null && <strong> — {money(item.amount)}</strong>}
                  {item.note && <span style={{ display: "block", fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight: 1.5 }}>{item.note}</span>}
                </span>
                {item.daysLeft != null && (
                  <span style={{ fontFamily: FONT_UI, fontSize: 12, fontWeight: 700, color: item.daysLeft < 0 ? RED : item.daysLeft <= 30 ? AMBER : T.textMuted, whiteSpace: "nowrap" }}>
                    {item.daysLeft < 0 ? `${Math.abs(item.daysLeft)} days late` : `${item.daysLeft} days`}
                  </span>
                )}
              </div>
            ))}
          </div>

          <div style={card}>
            <span style={label}>Correct anything NofoDesk misread</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 8 }}>
              <Field {...{ label, input }} title="Federal award date" value={award?.awardDate || ""} type="date"
                onChange={(v) => recompute({ ...award, awardDate: v })} />
              <Field {...{ label, input }} title="Budget period ends" value={award?.budgetPeriod?.end || ""} type="date"
                onChange={(v) => recompute({ ...award, budgetPeriod: { ...award.budgetPeriod, end: v } })} />
              <Field {...{ label, input }} title="Indirect rate %" value={award?.indirect?.ratePct ?? ""} type="number"
                onChange={(v) => recompute({ ...award, indirect: { ...award.indirect, ratePct: v } })} />
              <Field {...{ label, input }} title="SAM.gov registration expires" value={award?.recipient?.samExpires || ""} type="date"
                onChange={(v) => recompute({ ...award, recipient: { ...award.recipient, samExpires: v } })} />
            </div>
            <p style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, margin: "12px 0 0" }}>
              Every date above recalculates from the award date; nothing is guessed.
            </p>
          </div>

          <div style={{ ...card, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <button style={btn(true)} onClick={saveAward} disabled={saving}>{saving ? "Saving…" : award?.id ? "Save changes" : "Save this award"}</button>
            <span style={{ fontFamily: FONT_UI, fontSize: 13, color: savedMsg.startsWith("Saved") ? GREEN : RED }}>{savedMsg}</span>
            <span style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted }}>A saved award can have its actuals imported under Budget vs Actual.</span>
          </div>
        </>
      )}
    </div>
  );
}

const Figure = ({ T, card, label, title, value, note }) => (
  <div style={card}>
    <span style={label}>{title}</span>
    <div style={{ fontFamily: FONT_H, fontSize: 22, fontWeight: 700, color: T.text }}>{value}</div>
    {note && <div style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, marginTop: 4 }}>{note}</div>}
  </div>
);

const Field = ({ label, input, title, value, onChange, type = "text", placeholder }) => (
  <div>
    <span style={label}>{title}</span>
    <input style={input} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </div>
);

// ── TAB 2 — BUDGET CALL ──────────────────────────────────────
function BudgetCall({ T, card, label, input, btn, funders, award }) {
  const [ceiling, setCeiling] = useState("");
  const [funderId, setFunderId] = useState("cdc");
  const [ratePct, setRatePct] = useState("");
  const [excessOver, setExcessOver] = useState("");   // blank = the funder pack's default
  const [departments, setDepartments] = useState([
    { id: "d1", name: "", weight: 1, ceiling: null },
    { id: "d2", name: "", weight: 1, ceiling: null },
  ]);
  const [consolidated, setConsolidated] = useState(null);
  const [issues, setIssues] = useState([]);
  const [trim, setTrim] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (award?.amounts?.total && !ceiling) setCeiling(String(award.amounts.total));
    if (award?.indirect?.ratePct && !ratePct) setRatePct(String(award.indirect.ratePct));
    if (award?.funderId) setFunderId(award.funderId);
  }, [award]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDept = (i, patch) => setDepartments((ds) => ds.map((d, n) => (n === i ? { ...d, ...patch } : d)));

  const splitCeiling = async () => {
    setBusy(true);
    try {
      const weights = Object.fromEntries(departments.filter((d) => d.name).map((d) => [d.id, Number(d.weight) || 0]));
      const res = await apiFetch(`${API}/budget-call/ceilings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ total: Number(ceiling), weights }),
      });
      const data = await res.json();
      if (res.ok) setDepartments((ds) => ds.map((d) => ({ ...d, ceiling: data.ceilings[d.id] ?? d.ceiling })));
    } catch { /* leave the ceilings as they were */ }
    setBusy(false);
  };

  const consolidate = async () => {
    setBusy(true);
    try {
      const call = {
        departments: departments.filter((d) => d.name).map((d) => ({ id: d.id, name: d.name, ceiling: d.ceiling })),
        drafts: Object.fromEntries(departments.filter((d) => d.name).map((d) => [d.id, { status: d.status || "not started", lines: d.lines || [] }])),
      };
      const res = await apiFetch(`${API}/budget-call/consolidate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ call, awardCeiling: Number(ceiling) || null, funderId, indirectRatePct: Number(ratePct) || 0, excessOver: excessOver === "" ? null : Number(excessOver) }),
      });
      const data = await res.json();
      if (res.ok) { setConsolidated(data.consolidated); setIssues(data.issues || []); setTrim(null); }
    } catch { /* nothing to show */ }
    setBusy(false);
  };

  const proposeTrim = async () => {
    if (!consolidated?.overCeiling) return;
    const res = await apiFetch(`${API}/budget-call/propose-trim`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lines: consolidated.lines, gap: consolidated.gap }),
    });
    const data = await res.json();
    if (res.ok) setTrim(data);
  };

  const pack = funders.find((f) => f.id === funderId);

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={card}>
        <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 6px" }}>Set the ceilings</h2>
        <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, margin: "0 0 14px", lineHeight: 1.55 }}>
          Split the award across the departments that will draft. The parts always add back up to the ceiling exactly — no rounding dust.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 14 }}>
          <Field {...{ label, input }} title="Award ceiling" value={ceiling} type="number" onChange={setCeiling} />
          <div>
            <span style={label}>Funder</span>
            <select style={input} value={funderId} onChange={(e) => setFunderId(e.target.value)}>
              {funders.map((f) => <option key={f.id} value={f.id}>{f.short || f.name}</option>)}
            </select>
          </div>
          <Field {...{ label, input }} title="Indirect rate %" value={ratePct} type="number" onChange={setRatePct} />
          {pack?.indirect?.excessOver != null && (
            <Field {...{ label, input }} title="Subaward amount in base" value={excessOver} type="number" onChange={setExcessOver}
              placeholder={String(pack.indirect.excessOver)} />
          )}
        </div>
        {pack?.indirect && (
          <p style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, margin: "0 0 14px" }}>
            {pack.short} applies indirect to <strong>{pack.indirect.base}</strong>
            {pack.indirect.base === "MTDC" && " — equipment, tuition and the large part of each subaward sit outside the base."}
            {pack.indirect.excessOver != null && ` The first $${Number(excessOver === "" ? pack.indirect.excessOver : excessOver).toLocaleString()} of each subaward stays in the base (2 CFR 200.1). If your NICRA sets a different amount, enter it above.`}
          </p>
        )}

        {departments.map((d, i) => (
          <div key={d.id} style={{ display: "grid", gridTemplateColumns: "1fr 90px 130px 90px", gap: 10, marginBottom: 8, alignItems: "end" }}>
            <Field {...{ label, input }} title={i === 0 ? "Department" : ""} value={d.name} onChange={(v) => setDept(i, { name: v })} />
            <Field {...{ label, input }} title={i === 0 ? "Share" : ""} value={d.weight} type="number" onChange={(v) => setDept(i, { weight: v })} />
            <div>
              {i === 0 && <span style={label}>Ceiling</span>}
              <div style={{ ...input, background: "transparent", fontFamily: FONT_MONO }}>{d.ceiling == null ? "—" : money(d.ceiling)}</div>
            </div>
            <div>
              {i === 0 && <span style={label}>Status</span>}
              <select style={input} value={d.status || "not started"} onChange={(e) => setDept(i, { status: e.target.value })}>
                {["not started", "drafting", "submitted"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
          <button style={btn(false)} onClick={() => setDepartments((ds) => [...ds, { id: `d${ds.length + 1}`, name: "", weight: 1, ceiling: null }])}>Add a department</button>
          <button style={btn(true)} disabled={busy || !ceiling} onClick={splitCeiling}>Split the ceiling</button>
          <button style={btn(false)} disabled={busy} onClick={consolidate}>Consolidate</button>
        </div>
      </div>

      {consolidated && (
        <div style={card}>
          <span style={label}>Where the call stands</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, margin: "10px 0 16px" }}>
            <Stat {...{ T }} title="Submitted" value={`${consolidated.submittedCount} of ${consolidated.departmentCount}`} />
            <Stat {...{ T }} title="Consolidated total" value={money(consolidated.total)} />
            <Stat {...{ T }} title={consolidated.overCeiling ? "Over ceiling by" : "Under ceiling by"} value={money(Math.abs(consolidated.gap || 0))}
              tone={consolidated.overCeiling ? RED : GREEN} />
          </div>

          {consolidated.byDepartment.map((d) => (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderTop: `1px solid ${T.border}`, fontFamily: FONT_UI, fontSize: 13 }}>
              <span style={{ color: T.textBody }}>
                {d.name}
                <span style={{ color: d.status === "submitted" ? T.textMuted : AMBER, marginLeft: 8, fontSize: 12 }}>{d.status}</span>
              </span>
              <span style={{ fontFamily: FONT_MONO, color: d.overBy > 0 ? RED : T.textBody }}>
                {money(d.total)}{d.ceiling != null && <span style={{ color: T.textMuted }}> / {money(d.ceiling)}</span>}
              </span>
            </div>
          ))}

          {!!issues.length && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
              {issues.map((s, i) => (
                <div key={i} style={{ fontFamily: FONT_UI, fontSize: 13, color: s.level === "error" ? RED : AMBER, padding: "4px 0", lineHeight: 1.5 }}>{s.message}</div>
              ))}
            </div>
          )}

          {consolidated.overCeiling && (
            <div style={{ marginTop: 16 }}>
              <button style={btn(true)} onClick={proposeTrim}>Propose a trim</button>
              {trim && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, lineHeight: 1.55 }}>{trim.note}</p>
                  {trim.proposals.slice(0, 12).map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted }}>
                      <span>{p.department} — {p.description}</span>
                      <span style={{ fontFamily: FONT_MONO }}>{money(p.from)} → {money(p.to)}</span>
                    </div>
                  ))}
                  <p style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, marginTop: 10 }}>
                    A proposal, not a cut. Applying it records each reduction with its reason, which is what the justification is written from.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ T, title, value, tone }) => (
  <div>
    <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 4 }}>{title}</div>
    <div style={{ fontFamily: FONT_H, fontSize: 19, fontWeight: 700, color: tone || T.text }}>{value}</div>
  </div>
);


// ── TAB 3 — AGREEMENTS ───────────────────────────────────────
// One template, many sites. The template stays exactly as its owner wrote
// it; only the parts that change per site are filled in.
const SOURCES = [
  { value: "site.legal_name",          label: "Site name" },
  { value: "site.state_display",       label: "State (as printed)" },
  { value: "site.lga",                 label: "LGA" },
  { value: "site.site_id",             label: "Site ID" },
  { value: "state.manager_block",      label: "State manager block (multi-line)" },
  { value: "state.manager_name",       label: "State manager name" },
  { value: "state.manager_email",      label: "State manager email" },
  { value: "round.period_text",        label: "Period, as words" },
  { value: "round.title_text",         label: "Round title" },
  { value: "funding.amount",           label: "Amount, in figures" },
  { value: "funding.amount_words",     label: "Amount, in words" },
  { value: "funding.amount_full",      label: "Amount, figures and words" },
  { value: "funding.cumulative",       label: "Cumulative amount, in figures" },
  { value: "doc.number",               label: "Document number" },
  { value: "settings.org_name",        label: "Your organisation" },
  { value: "settings.program_name",    label: "Programme name" },
  { value: "settings.signatory_name",  label: "Signatory name" },
  { value: "settings.signatory_title", label: "Signatory title" },
  { value: "settings.fy_label",        label: "Financial year" },
];

// A first guess at what each placeholder means, from what it is called.
// Always shown for correction: a wrong guess in a signed agreement is not
// something you can take back.
function guessSource(token) {
  const t = token.toLowerCase();
  if (/word.*amount|amount.*word|in words/.test(t)) return "funding.amount_full";
  if (/facility|site|partner|clinic|hospital/.test(t) && /name/.test(t)) return "site.legal_name";
  if (/manager|focal|contact/.test(t)) return "state.manager_block";
  if (/state|province|region/.test(t)) return "site.state_display";
  if (/cumulative|revised/.test(t)) return "funding.cumulative";
  if (/amount|payment|sum/.test(t)) return "funding.amount";
  if (/doc.?no|number|ref/.test(t)) return "doc.number";
  if (/period|date/.test(t)) return "round.period_text";
  if (/signator|ceo|director|chief/.test(t)) return "settings.signatory_name";
  if (/org|institute|trust/.test(t)) return "settings.org_name";
  if (/year/.test(t)) return "settings.fy_label";
  return "";
}

const isBlockToken = (t) => t.block && /budget|target|table|image/i.test(t.token);

function Agreements({ T, card, label, input, btn }) {
  const [template, setTemplate] = useState(null);
  const [mapping, setMapping]   = useState({});
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [result, setResult] = useState(null);
  const [sheet, setSheet]   = useState("");
  const [states, setStates] = useState([]);
  const [meta, setMeta] = useState({
    org_name: "", org_code: "", fy_label: "", signatory_name: "", signatory_title: "",
    round_id: "LOA", kind: "agreement", period_text: "", currency_code: "NGN",
    numberPattern: "{ORG}/{STATE}/{ROUND}/{FY}/{SEQ:04}",
  });

  const readTemplate = async (file) => {
    setBusy(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(`${API}/agreements/template`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "NofoDesk could not read that template.");
      setTemplate({ file, tokens: data.tokens, name: data.name });
      setMapping(Object.fromEntries(data.tokens.filter((t) => !isBlockToken(t)).map((t) => [t.token, guessSource(t.token)])));
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  // Sites pasted straight from the spreadsheet they already live in.
  const sites = sheet.split(/\r?\n/)
    .map((l) => l.split(/\t|,/).map((c) => c.trim().replace(/^"|"$/g, "")))
    .filter((c) => c[0])
    .map((c, i) => ({
      site_id: `SITE-${String(i + 1).padStart(4, "0")}`,
      legal_name: c[0], state_code: (c[1] || "").toUpperCase(), site_type: "SITE", include: true,
      amount: Number(String(c[2] || "").replace(/[^\d.]/g, "")) || 0,
    }));
  const stateCodes = [...new Set(sites.map((s) => s.state_code).filter(Boolean))];

  useEffect(() => {
    // A state appears here the moment a site names it, so nobody discovers a
    // missing manager block halfway through a batch.
    setStates((prev) => stateCodes.map((code) =>
      prev.find((p) => p.state_code === code)
      || { state_code: code, state_name: code, state_display: code, manager_name: "", manager_title: "", manager_email: "", active: true }));
  }, [sheet]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildRun = () => ({
    round_id: meta.round_id,
    numberPattern: meta.numberPattern,
    settings: {
      org_name: meta.org_name,
      org_code: meta.org_code || meta.org_name.replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase(),
      fy_label: meta.fy_label, signatory_name: meta.signatory_name,
      signatory_title: meta.signatory_title, currency_code: meta.currency_code,
    },
    overrides: [],
    states,
    sites,
    rounds: [{ round_id: meta.round_id, kind: meta.kind, sequence_no: 1, period_text: meta.period_text, template: template?.name || "" }],
    funding: sites.map((s) => ({ site_id: s.site_id, round_id: meta.round_id, amount: s.amount })),
    profiles: {},
    placeholders: Object.entries(mapping).filter(([, src]) => src).map(([token, source]) => ({ token, source })),
    budgetToken: (template?.tokens || []).filter(isBlockToken).map((t) => t.token)[0] || "{{budget_table}}",
  });

  const generate = async (previewOnly) => {
    setBusy(true); setError(""); setResult(null);
    try {
      const run = buildRun();
      const fd = new FormData();
      fd.append("template", template.file);
      fd.append("run", JSON.stringify(previewOnly ? { ...run, sites: sites.slice(0, 1) } : run));
      const res = await apiFetch(`${API}/agreements/${previewOnly ? "preview" : "generate"}`, { method: "POST", body: fd });
      if (!res.ok) {
        const data = await res.json();
        const lines = [...(data.errors || []).map((e) => e.message), ...(data.warnings || []).map((w) => w.message)];
        throw new Error(lines.length ? lines.join(" ") : (data.error || "That run could not be completed."));
      }
      const blob = await res.blob();
      if (previewOnly) {
        const notes = JSON.parse(res.headers.get("X-Preview-Notes") || "{}");
        saveFile(blob, `${sites[0].legal_name} - preview.docx`);
        setResult({ preview: true, ...notes });
      } else {
        const summary = JSON.parse(res.headers.get("X-Run-Summary") || "{}");
        saveFile(blob, `${meta.round_id}-${new Date().toISOString().slice(0, 10)}.zip`);
        setResult(summary);
      }
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const unmapped = Object.entries(mapping).filter(([, src]) => !src).map(([t]) => t);
  const ready = template && sites.length && states.every((s) => s.manager_name && s.manager_email) && !unmapped.length && meta.org_name;
  const blocked = unmapped.length ? `${unmapped.length} placeholder${unmapped.length === 1 ? "" : "s"} still need a source`
    : !meta.org_name ? "Add your organisation's name"
    : !sites.length ? "Paste your sites above"
    : "Each state needs a manager name and email";

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={card}>
        <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 6px" }}>1 · Your own template</h2>
        <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, margin: "0 0 14px", lineHeight: 1.55 }}>
          Upload the Word letter you already use. Your letterhead, wording and signature block stay exactly as they are. NofoDesk finds the placeholders even where Word has split one into pieces, which is why find-and-replace misses them.
        </p>
        <label style={{ ...btn(true), display: "inline-block" }}>
          {busy && !template ? "Reading…" : template ? "Choose a different template" : "Choose the Word template"}
          <input type="file" accept=".docx" style={{ display: "none" }} disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) readTemplate(f); e.target.value = ""; }} />
        </label>

        {template && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <span style={label}>{template.tokens.length} placeholders in {template.name} — check what each one means</span>
            {template.tokens.map((t) => (
              <div key={t.token} style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1fr) 1fr", gap: 10, alignItems: "center", padding: "7px 0", borderTop: `1px solid ${T.border}` }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.textBody }}>
                  {t.token}<span style={{ color: T.textMuted }}> ×{t.count}</span>
                </span>
                {isBlockToken(t) ? (
                  <span style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted }}>
                    Budget table — removed when a site has no budget lines set up
                  </span>
                ) : (
                  <select style={{ ...input, borderColor: mapping[t.token] ? T.border : AMBER }}
                    value={mapping[t.token] || ""} onChange={(e) => setMapping((m) => ({ ...m, [t.token]: e.target.value }))}>
                    <option value="">— pick what goes here —</option>
                    {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={card}>
        <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 6px" }}>2 · Your sites</h2>
        <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, margin: "0 0 10px", lineHeight: 1.55 }}>
          Paste from your spreadsheet: site name, state code, amount — one site per line.
        </p>
        <textarea
          style={{ ...input, minHeight: 130, fontFamily: FONT_MONO, fontSize: 12.5, lineHeight: 1.6 }}
          placeholder={"Example Primary Health Centre\tNOR\t174825\nSecond Example Clinic\tNOR\t174825"}
          value={sheet} onChange={(e) => setSheet(e.target.value)} />
        {!!sites.length && (
          <p style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, marginTop: 10 }}>
            {sites.length} sites · {stateCodes.length} states · total {money(sites.reduce((s, x) => s + x.amount, 0), meta.currency_code)}
          </p>
        )}

        {!!states.length && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <span style={label}>Who receives and signs in each state</span>
            {states.map((st, i) => (
              <div key={st.state_code} style={{ display: "grid", gridTemplateColumns: "70px 1fr 1fr 1fr", gap: 8, marginTop: 8, alignItems: "center" }}>
                <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.textBody }}>{st.state_code}</span>
                {["state_display", "manager_name", "manager_email"].map((f) => (
                  <input key={f} style={{ ...input, borderColor: f === "state_display" || st[f] ? T.border : AMBER }}
                    placeholder={{ state_display: "As printed", manager_name: "Manager name", manager_email: "Manager email" }[f]}
                    value={st[f]} onChange={(e) => setStates((ss) => ss.map((x, n) => (n === i ? { ...x, [f]: e.target.value } : x)))} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={card}>
        <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 12px" }}>3 · This round</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <Field {...{ label, input }} title="Your organisation" value={meta.org_name} onChange={(v) => setMeta((m) => ({ ...m, org_name: v }))} />
          <Field {...{ label, input }} title="Round (LOA, MOD1…)" value={meta.round_id} onChange={(v) => setMeta((m) => ({ ...m, round_id: v }))} />
          <div>
            <span style={label}>Kind</span>
            <select style={input} value={meta.kind} onChange={(e) => setMeta((m) => ({ ...m, kind: e.target.value }))}>
              <option value="agreement">Agreement</option>
              <option value="modification">Modification</option>
            </select>
          </div>
          <Field {...{ label, input }} title="Period, as words" value={meta.period_text} onChange={(v) => setMeta((m) => ({ ...m, period_text: v }))} />
          <Field {...{ label, input }} title="Financial year" value={meta.fy_label} onChange={(v) => setMeta((m) => ({ ...m, fy_label: v }))} />
          <Field {...{ label, input }} title="Signatory" value={meta.signatory_name} onChange={(v) => setMeta((m) => ({ ...m, signatory_name: v }))} />
          <Field {...{ label, input }} title="Number pattern" value={meta.numberPattern} onChange={(v) => setMeta((m) => ({ ...m, numberPattern: v }))} />
          <div>
            <span style={label}>Currency</span>
            <select style={input} value={meta.currency_code} onChange={(e) => setMeta((m) => ({ ...m, currency_code: e.target.value }))}>
              {["NGN", "USD", "GBP", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap", alignItems: "center" }}>
          <button style={btn(false)} disabled={!template || !sites.length || busy} onClick={() => generate(true)}>Preview one letter</button>
          <button style={btn(true)} disabled={!ready || busy} onClick={() => generate(false)}>
            {busy ? "Generating…" : `Generate ${sites.length || ""} letters`}
          </button>
          {!ready && template && <span style={{ fontFamily: FONT_UI, fontSize: 12.5, color: AMBER }}>{blocked}</span>}
        </div>
        {error && <div style={{ marginTop: 12, color: RED, fontFamily: FONT_UI, fontSize: 13, lineHeight: 1.55 }}>{error}</div>}
      </div>

      {result && (
        <div style={{ ...card, borderLeft: `3px solid ${result.failed ? AMBER : GREEN}` }}>
          <span style={label}>{result.preview ? "Preview" : "Run finished"}</span>
          {result.preview ? (
            <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textBody, margin: "6px 0 0", lineHeight: 1.6 }}>
              One letter downloaded. {result.ok ? "Every placeholder was filled." : "Some placeholders could not be filled — check the list above."}
              {result.total != null && ` Its budget table totals ${money(result.total, meta.currency_code)}.`}
            </p>
          ) : (
            <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textBody, margin: "6px 0 0", lineHeight: 1.6 }}>
              {result.generated} generated{result.skipped ? `, ${result.skipped} skipped` : ""}{result.failed ? `, ${result.failed} failed` : ""}.
              The zip holds one letter per site, foldered by state, with a manifest and a run log naming every site and what happened to it.
            </p>
          )}
        </div>
      )}

      <div style={{ ...card, borderLeft: `3px solid ${TOKENS.gold}` }}>
        <span style={label}>What is true of every batch</span>
        <ul style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, lineHeight: 1.7, margin: "8px 0 0", paddingLeft: 18 }}>
          <li>Each letter is numbered once, and keeps that number if you regenerate it.</li>
          <li>A modification carries its original agreement's number on its face.</li>
          <li>The amount in words is written from the same number printed in figures, so the two cannot disagree.</li>
          <li>A letter with a placeholder left unfilled is never written at all.</li>
          <li>One site failing never stops the rest — every site appears in the run log with a reason.</li>
        </ul>
      </div>
    </div>
  );
}
